import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  collection,
  getDoc,
  setDoc,
  doc,
  serverTimestamp,
  onSnapshot,
} from "firebase/firestore";
import { db } from "../utils/firebase";
import { useAuth } from "../../../context/AuthContext";
import { toast } from "react-toastify";

type GenStatus = "queued" | "running" | "ready" | "failed";

interface DocItem {
  id: string;
  title: string;
  preview: string;
  category: string;
  updatedAt: Date | null;
  genStatus?: GenStatus;
  genError?: string;
}

interface Category { id: string; name: Record<string, string>; }
const LANG = "en";

const GEN_FN_URL = "https://us-central1-ama-top.cloudfunctions.net/generateArticle";

const LENGTH_OPTIONS: Array<{ id: "short" | "medium" | "long"; label: string; hint: string }> = [
  { id: "short",  label: "Short",  hint: "~300–500 words" },
  { id: "medium", label: "Medium", hint: "~600–900 words" },
  { id: "long",   label: "Long",   hint: "~1200–1800 words" },
];

const ContentList: React.FC = () => {
  const { project, contentType } = useParams<{ project: string; contentType: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [docs, setDocs] = useState<DocItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [genOpen, setGenOpen] = useState(false);

  useEffect(() => {
    if (!project || !contentType) return;
    setLoading(true);

    getDoc(doc(db, "content", project, "_config", "insights"))
      .then((snap) => {
        if (snap.exists()) {
          const raw = snap.data().categories || [];
          setCategories(raw.map((c: any) =>
            typeof c === "string" ? { id: c.toLowerCase().replace(/[^a-z0-9]+/g, "-"), name: { [LANG]: c } } : c
          ));
        }
      })
      .catch(() => {});

    const unsub = onSnapshot(
      collection(db, "content", project, contentType),
      (snap) => {
        const items: DocItem[] = [];
        snap.forEach((d) => {
          if (d.id.startsWith("_")) return;
          const data = d.data();
          const rawTitle = typeof data.title === "object" ? data.title?.en : data.title;
          items.push({
            id: d.id,
            title: rawTitle || data.generation?.topic || d.id,
            preview: data.preview || "",
            category: data.category || data.generation?.category || "",
            updatedAt: data.updatedAt?.toDate?.() || data.createdAt?.toDate?.() || null,
            genStatus: data.generation?.status,
            genError: data.generation?.error,
          });
        });
        items.sort((a, b) => {
          if (a.updatedAt && b.updatedAt) return b.updatedAt.getTime() - a.updatedAt.getTime();
          return a.title.localeCompare(b.title);
        });
        setDocs(items);
        setLoading(false);
      },
      (err) => {
        console.error("[ContentList] Snapshot error:", err);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [project, contentType]);

  const createDoc = async () => {
    if (!project || !contentType) return;
    setCreating(true);
    const id = `${contentType}_${Date.now()}`;
    try {
      await setDoc(doc(db, "content", project, contentType, id), {
        title: {},
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: user?.email || "unknown",
      });
      navigate(`/cms/${project}/${contentType}/${id}`);
    } catch (err: any) {
      console.error("[ContentList] Create error:", err);
      toast.error("Failed to create");
    }
    setCreating(false);
  };

  if (loading) return <div className="editor__loading">Loading...</div>;

  return (
    <div className="content-list">
      <div className="content-list__header">
        <h2 className="content-list__title">{contentType}</h2>
        <div className="content-list__actions">
          <button
            className="editor__save-btn editor__save-btn--secondary"
            onClick={() => setGenOpen(true)}
          >
            ✨ Generate
          </button>
          <button className="editor__save-btn" onClick={createDoc} disabled={creating}>
            {creating ? "Creating..." : "+ New"}
          </button>
        </div>
      </div>

      {docs.length === 0 ? (
        <div className="content-list__empty">No items yet</div>
      ) : (
        (() => {
          const grouped = new Map<string, DocItem[]>();
          for (const d of docs) {
            const key = d.category || "";
            if (!grouped.has(key)) grouped.set(key, []);
            grouped.get(key)!.push(d);
          }
          const catOrder = categories.map((c) => c.id);
          const sortedKeys = Array.from(grouped.keys()).sort((a, b) => {
            const ai = catOrder.indexOf(a);
            const bi = catOrder.indexOf(b);
            if (ai >= 0 && bi >= 0) return ai - bi;
            if (ai >= 0) return -1;
            if (bi >= 0) return 1;
            return a.localeCompare(b);
          });

          return sortedKeys.map((catId) => {
            const catLabel = categories.find((c) => c.id === catId)?.name[LANG] || catId || "Uncategorized";
            return (
              <div key={catId} className="content-list__group">
                <h3 className="content-list__group-title">{catLabel}</h3>
                <div className="content-list__items">
                  {grouped.get(catId)!.map((d) => {
                    const genActive = d.genStatus === "queued" || d.genStatus === "running";
                    return (
                      <button
                        key={d.id}
                        className={`content-list__item ${genActive ? "content-list__item--locked" : ""}`}
                        onClick={() => {
                          if (genActive) {
                            toast.info("Generation in progress — open when ready");
                            return;
                          }
                          navigate(`/cms/${project}/${contentType}/${d.id}`);
                        }}
                        aria-disabled={genActive}
                      >
                        {d.preview && (
                          <img src={d.preview} alt="" className="content-list__item-preview" />
                        )}
                        <span className="content-list__item-title">{d.title || "Untitled"}</span>
                        {genActive && (
                          <span className="content-list__item-badge content-list__item-badge--gen">
                            {d.genStatus === "queued" ? "Queued…" : "Generating…"}
                          </span>
                        )}
                        {d.genStatus === "failed" && (
                          <span
                            className="content-list__item-badge content-list__item-badge--failed"
                            title={d.genError || "generation failed"}
                          >
                            Failed
                          </span>
                        )}
                        {d.updatedAt && (
                          <span className="content-list__item-date">
                            {d.updatedAt.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          });
        })()
      )}

      {genOpen && project && contentType && (
        <GenerateModal
          project={project}
          contentType={contentType}
          categories={categories}
          userEmail={user?.email || "unknown"}
          onClose={() => setGenOpen(false)}
        />
      )}
    </div>
  );
};

interface GenerateModalProps {
  project: string;
  contentType: string;
  categories: Category[];
  userEmail: string;
  onClose: () => void;
}

const GenerateModal: React.FC<GenerateModalProps> = ({ project, contentType, categories, userEmail, onClose }) => {
  const [topic, setTopic] = useState("");
  const [category, setCategory] = useState("");
  const [length, setLength] = useState<"short" | "medium" | "long">("medium");
  const [tone, setTone] = useState("");
  const [notes, setNotes] = useState("");
  const [locale, setLocale] = useState("en");
  const [submitting, setSubmitting] = useState(false);
  const [styleLoaded, setStyleLoaded] = useState(false);
  const [hasRules, setHasRules] = useState(false);

  useEffect(() => {
    getDoc(doc(db, "content", project, "_config", "ai_generation"))
      .then((snap) => {
        if (snap.exists()) {
          const cfg = (snap.data() as any)[contentType] || {};
          if (cfg.defaultLength) setLength(cfg.defaultLength);
          if (cfg.defaultTone) setTone(cfg.defaultTone);
          if (cfg.defaultLocale) setLocale(cfg.defaultLocale);
          setHasRules(!!cfg.rules);
        }
      })
      .catch(() => {})
      .finally(() => setStyleLoaded(true));
  }, [project, contentType]);

  const submit = async () => {
    const t = topic.trim();
    if (!t) {
      toast.error("Topic is required");
      return;
    }
    setSubmitting(true);
    const docId = `${contentType}_${Date.now()}`;
    try {
      const res = await fetch(GEN_FN_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          project,
          contentType,
          docId,
          topic: t,
          category,
          tone: tone.trim(),
          length,
          locale,
          notes: notes.trim(),
          user: userEmail,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      toast.success(`Queued: "${t.slice(0, 40)}${t.length > 40 ? "…" : ""}"`);
      onClose();
    } catch (err: any) {
      console.error("[Generate] Failed:", err);
      toast.error(`Generate failed: ${err.message}`);
    }
    setSubmitting(false);
  };

  return (
    <div className="cms-modal__overlay" onClick={onClose}>
      <div className="cms-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cms-modal__header">
          <h3 className="cms-modal__title">Generate article</h3>
          <button className="cms-modal__close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="cms-modal__body">
          {styleLoaded && !hasRules && (
            <div className="cms-modal__warn">
              No project-level rules set. Configure them in <strong>AI Generation</strong> tab for consistent results.
            </div>
          )}
          <label className="cms-modal__field">
            <span>Topic *</span>
            <input
              type="text"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="e.g. How magnesium helps with sleep"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit();
              }}
            />
          </label>

          <div className="cms-modal__field">
            <span>Length</span>
            <div className="cms-modal__radio-group">
              {LENGTH_OPTIONS.map((opt) => (
                <label key={opt.id} className={`cms-modal__radio ${length === opt.id ? "cms-modal__radio--active" : ""}`}>
                  <input
                    type="radio"
                    name="length"
                    checked={length === opt.id}
                    onChange={() => setLength(opt.id)}
                  />
                  <span className="cms-modal__radio-label">{opt.label}</span>
                  <span className="cms-modal__radio-hint">{opt.hint}</span>
                </label>
              ))}
            </div>
          </div>

          <label className="cms-modal__field">
            <span>Tone</span>
            <input
              type="text"
              value={tone}
              onChange={(e) => setTone(e.target.value)}
              placeholder="clinical, directive, anti-marketing"
            />
          </label>

          {categories.length > 0 && (
            <label className="cms-modal__field">
              <span>Category</span>
              <select value={category} onChange={(e) => setCategory(e.target.value)}>
                <option value="">— None —</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name[LANG] || c.id}</option>
                ))}
              </select>
            </label>
          )}

          <label className="cms-modal__field">
            <span>Extra notes</span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Anything else: must-mention points, audience, what to avoid…"
              rows={3}
            />
          </label>
        </div>

        <div className="cms-modal__footer">
          <span className="cms-modal__hint">⌘+Enter to queue</span>
          <div className="cms-modal__actions">
            <button className="editor__back" onClick={onClose}>Cancel</button>
            <button className="editor__save-btn" onClick={submit} disabled={submitting || !topic.trim()}>
              {submitting ? "Queuing…" : "Queue"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentList;
